import {classNames, InfiniteScroll, React, useLocalStore, observer, useAsObservableSource, useEffect} from '@api'
import {Chip, Card, CardContent, Divider} from '@material-ui/core'
import {Close} from '@material-ui/icons'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance, useCatalog} from '@stores'
import {action, autorun, computed, observable, reaction, runInAction} from 'mobx'
import {FieldRendererStore, useFieldRendererStore} from '@design-board/stores'
import {useContext} from 'react'
import {PrimaryIconButton} from 'centric-ui-components'
import {Icon, InputGroup, Popover, Spinner} from '@blueprintjs/core'
import {Button} from '@blueprintjs/core'
import {useAsync} from 'react-use'
import {LoadingIndicator} from '@modules/common'
import * as css from './TagListChipsRenderer.css'
import { Popover2 } from '@api'

/// region Store

class TagListChipsRendererStore extends VipLocalStore {
  @observable results: Array<any> = []
  @observable total: number = 0
  @observable isSearch: boolean = false
  @observable search: string = ''

  get tagType() { return this.fieldStore.catalogField?.def?.tagType }

  constructor(stores: StoresInstance,public fieldStore: FieldRendererStore) {
    super(stores)
  }

  registerEffects = () => {
    const {fieldStore: {field}, stores: {tags, catalog}} = this

    const toDispose = [
      autorun(async () => {
        const {valuesRaw} = this

        let r = _.cloneDeep(valuesRaw)

        // YFTS-8685
        // For some records the tag ID is stored but not the name (which can change anyways).  Lookup the name by ID
        //
        for (var v of r) {
          v.name = v.name ?? (await tags.tagById(catalog.idFor(v, false)))?.name
        }

        this.values = r
      }),
      reaction(
        () => this.search,
        _.debounce(() => {
          this.runQuery(0)
        }, 500),
        {name: `Run tag list search for ${field.field} after search text changes`}
      )
    ]

    return () => toDispose.forEach(f => f())
  }

  @computed get hasMore() {
    return !this.results || this.results.length < this.total
  }

  @computed get availableData() {
    const {fieldStore: {value}, data} = this
    return _.differenceBy(data || [], value?.val, 'id')
  }

  @computed get data () {
    return this.results?.map(d => d._source)
  }

  @computed get label() {
    return this.catalog.labelForFieldName_currentCatalog(this.fieldStore.field.field, 'plural')
  }

  @action runQuery = async (page: number) => {
    const {tagType, search, stores: {tags}} = this
    const {hits, total} = await tags.loadTagsFromEs({tagType, searchText: search})

    runInAction(() => {
      this.total = total.value
      this.results = !page || !this.results ? [...hits] : this.results.concat(hits)
    })
  }

  inputRef: HTMLInputElement
  saveInputRef = r => this.inputRef = r
  @action toggleSearch = () => {
    this.isSearch = !this.isSearch
    this.search = ''
    if (this.isSearch) {
      this.inputRef?.focus()
    }
  }

  @action _changeSearch = (e) => {
    this.search = e.target.value
  }

  @observable isAddMenuOpen = false

  @action onAddMenuOpen = () => {
    this.isAddMenuOpen = true
    this.fieldStore.itemDetailsStore?.freeze
  }
  @action reset = () => {
    this.isAddMenuOpen = false
    this.isSearch = false
    this.search = ''
    this.fieldStore.itemDetailsStore?.unfreeze()
  }

  @action addTag = (tag) => {
    const {fieldStore: {value: existing, field, itemDetailsStore: {updateFieldData}}} = this

    updateFieldData(field?.field, [...(existing?.val ?? []).map(v => ({id: v.id, name: v.name})), _.pick(tag, ['id', 'name'])])
  }

  @action onRemoveTag = (index) => e => {
  const {fieldStore: {itemDetailsStore: {updateFieldData}, field}, values} = this
    updateFieldData(field?.field, values.filter((v, i) => i !== index))
  }

  @observable values = []
  @computed private get valuesRaw() {
    const {fieldStore: {value}} = this
    let values = !value?.val ? [] : !_.isArray(value?.val) ? [value?.val].filter(_.identity) : value?.val

    return values
  }
}

export const TagListChipsRendererStoreContext = React.createContext<TagListChipsRendererStore>(undefined)
export const useTagListChipsRendererStore = () => useContext(TagListChipsRendererStoreContext)

/// endregion

export const TagListChipsRenderer = observer(function TagListChipsRenderer() {
  const fieldRendererStore = useFieldRendererStore(),
    {strings, field, editable, cardFieldStore, detailsStore} = fieldRendererStore,
    store = useVipLocalStore((ws) => new TagListChipsRendererStore(ws, fieldRendererStore)),
    {catalog, onRemoveTag, values, isAddMenuOpen, onAddMenuOpen} = store

  useEffect(() => store.registerEffects(), [])

  return <TagListChipsRendererStoreContext.Provider value={store}>
    <div className={css.root} data-type='TagListChipsRenderer' data-count={_.size(values)}>
      {_.isEmpty(values) ? !editable || cardFieldStore ? strings.noValue : null :
       <div className={css.chips} data-type="Chips">
         {values?.map(
           (v, i) =>
             <Chip key={v?.id ?? i}
                   label={`${v?.name}`}
                   data-type="Chip"
                   className={css.chip}
                   onDelete={editable && onRemoveTag(i)}
             />)}
       </div>}
      {editable &&
	   <Popover2
		   interactionKind="click"
		   className={css.specPopover}
		   onOpened={onAddMenuOpen}
		   onClose={store.reset}
		   position={detailsStore?.sidebar?.align === 'right' ? 'left' : 'right'}
	     content={<PopupList/>}>
         {!_.isEmpty(values) || cardFieldStore
          ? <PrimaryIconButton className={css.addMore} data-type='AddMoreButton' data-open={isAddMenuOpen} icon={<Icon icon='add'/>}/>
                                               : <Button className={css.addButton}>Add {catalog.labelForFieldName_currentCatalog(field.field, 'plural', detailsStore?.itemDetailsStore?.index)}</Button>}
	   </Popover2>}
    </div>
  </TagListChipsRendererStoreContext.Provider>
})

const PopupHeader = observer(function PopupHeader() {
  const {label, search, isSearch, saveInputRef, toggleSearch, _changeSearch, data: values, total, hasMore} = useTagListChipsRendererStore(),
    countHtml = _.isEmpty(values) ? '' : hasMore ? `(${values.length}/${total})` : `(${total})`
  return (
    <div className={css.header}>
      <div className={classNames(css.text, {[css.hide]: isSearch})}>
        <div>{label} {countHtml.toString()}</div>
      </div>
      <PrimaryIconButton onClick={toggleSearch} icon={<Icon icon={'search'}/>}/>
      <InputGroup
        inputRef={saveInputRef}
        rightElement={<PrimaryIconButton className={css.closeSearcher} icon={<Icon icon={'cross'}/>} onClick={toggleSearch}/>}
        className={classNames(css.searcher, {[css.hide]: !isSearch, [css.input]: isSearch})}
        value={search}
        onChange={_changeSearch}
        placeholder={`Search...`}
      />
    </div>)
})

const TagListIndividual = observer(function TagListIndividual ({data}: {data: any}) {
  const {editable} = useFieldRendererStore(),
    {addTag, fieldStore: {value}, values, onRemoveTag} = useTagListChipsRendererStore(),
    selected = value?.val?.map(v => v.name) ?? [],
    is = selected.includes(data.name),
    index = values?.findIndex(v => v.name === data.name)

  return <div data-list-type="TagList" data-tag-selected={is} className={css.tagItem} key={data?.id} onClick={() => {!is && addTag(data)}}>
    <span>{data?.name}</span>
    {is && <PrimaryIconButton onClick={editable && onRemoveTag(index)} icon={<Close/>}/>}
  </div>
})

const PopupList = observer(function PopupList() {
  const {strings, availableData, runQuery, label, hasMore, addTag, fieldStore: {value}} = useTagListChipsRendererStore(),
    {loading} = useAsync(async () => runQuery(0))

  return <Card className={css.specList}>
    <PopupHeader/>
    <Divider/>
    <CardContent className={css.listContent} data-spec-list={label}>
      <InfiniteScroll
        className={css.infiniteScroll}
        pageStart={0}
        useWindow={false}
        hasMore={hasMore}
        loadMore={runQuery}
        loader={<Spinner key="spinner" size={20}/>}
      >
        {!_.isEmpty(availableData) ? availableData.map((d, i) => <div className={css.tagItem} key={d?.id} onClick={() => {addTag(d)}}>{d?.name}</div>)
                                   : !loading ? <div className={css.noResults}>{strings.noResults}</div> : <LoadingIndicator/>}
      </InfiniteScroll>
    </CardContent>
  </Card>
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/TagListChipsRenderer.tsx